import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import _ from 'lodash';

import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { DEFAULT_INITIAL_SUPPORT_STEP, FEATURES, INBOX_TYPES, routes } from 'src/app/constants';
import { generateRandomString } from 'src/app/utils/utils';
import { Customer, InboundEmailRule } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';

@Component({
  selector: 'app-project-email',
  templateUrl: './project-email.component.html',
  styleUrls: ['./project-email.component.scss'],
})
export class ProjectEmailComponent implements OnInit {
  inboundEmailRules: Partial<InboundEmailRule>[] | null;
  hasEmailRules = false;
  featureEnabled = true;
  blockedEmailField: string;
  validEmailDomains: any[] = [];
  showEmailInboxModal = false;
  inboxToEdit: Partial<InboundEmailRule>;
  isUpdatingInbox = false;
  projectId: string;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private customersService: CustomersService,
    private apiService: ApiService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private navController: NavController,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.INBOUND_EMAIL);

    await Promise.all([this.getInboundEmailRules(), this.loadEmailDomains()]);
  }

  async loadEmailDomains() {
    const emailDomainsResponse: any = await this.apiService.postPromise('/email-domains/list', {
      limit: null,
    });

    const domains = emailDomainsResponse.data ?? [];

    this.validEmailDomains = domains.filter((d) => d.valid);
  }

  getForwardEmail(rule: Partial<InboundEmailRule>) {
    return `${this.authService.tenantId}+${rule.shortId}@mail.unthread.io`;
  }

  addRule() {
    if (!this.featureEnabled) {
      this.navController.navigateRoot('upgrade');
      return;
    }

    this.inboxToEdit = {
      supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
      name: '',
      email: '',
      channelId: '',
      alwaysUseGenericEmailAddress: false,
      autoresponder: { enabled: false, condition: 'always', message: '' },
      shortId: generateRandomString(6),
      projectId: this.projectId,
    };
    this.showEmailInboxModal = true;
  }

  async getInboundEmailRules() {
    const result = await this.inboundEmailRulesService.list({
      order: ['createdAt'],
      descending: false,
      where: [
        {
          field: 'projectId',
          operator: '==',
          value: this.projectId,
        },
      ],
    });

    this.inboundEmailRules = result.data ?? null;
    this.hasEmailRules = !!this.inboundEmailRules.length;

    if (this.hasEmailRules) {
      this.getCustomers();
    }
  }

  async getCustomers() {
    if (this.inboundEmailRules?.length) {
      // It will be way easier to batch these, but we're movign them to postgres anyway so I'm not going to waste the time
      this.inboundEmailRules.forEach(async (rule) => {
        const customer: Partial<Customer> = await this.customersService.get(rule.customerId);
        rule.name = customer.name;
        rule.supportSteps = customer.supportSteps;
      });
    }
  }

  copyToClipboard(rule: Partial<InboundEmailRule>) {
    navigator.clipboard
      .writeText(this.getForwardEmail(rule))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async editWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }

  async save(rule: Partial<InboundEmailRule>) {
    try {
      const payload: Partial<InboundEmailRule> = rule;

      this.isUpdatingInbox = true;

      const result = payload?.id
        ? await this.inboundEmailRulesService.update(payload.id, payload)
        : await this.inboundEmailRulesService.create(payload);

      this.msgSrvc.show('✔︎ Saved');
      this.inboxToEdit = null;
      this.isUpdatingInbox = false;

      this.onDismissEmailInboxModal();
    } catch (e) {
      this.msgSrvc.showError(e);
      this.isUpdatingInbox = false;
    }
  }

  supportStepsDidChange(supportSteps: any[], rule: Partial<InboundEmailRule>) {
    rule.supportSteps = supportSteps;
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Disable email forwarding',
      subHeader: 'Are you sure you want to disable email forwarding?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            if (this.inboxToEdit) {
              this.isUpdatingInbox = true;

              await this.inboundEmailRulesService.delete(this.inboxToEdit.id);

              this.onDismissEmailInboxModal();

              this.isUpdatingInbox = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  addBlockedEmail(rule: Partial<InboundEmailRule>) {
    if (rule.blocklist) {
      rule.blocklist.push(this.blockedEmailField.toLowerCase());
    } else {
      rule.blocklist = [this.blockedEmailField.toLowerCase()];
    }

    this.blockedEmailField = '';
  }

  deleteBlocklistEntry(rule: Partial<InboundEmailRule>, index: number) {
    rule.blocklist = this.sortBlocklist(rule).filter((_, i) => i !== index);
  }

  showEmailHelpdesk() {
    window.open('https://docs.unthread.io/email/email-slack-connector', '_blank');
  }

  editInbox(inbox: Partial<InboundEmailRule>) {
    this.inboxToEdit = inbox;
    this.showEmailInboxModal = true;
    this.navController.navigateForward(this.router.url, {
      queryParams: {
        id: inbox.id,
      },
    });
  }

  cancelInboxEdit() {
    this.showEmailInboxModal = false;
    this.inboxToEdit = null;
  }

  viewInbox(rule: Partial<InboundEmailRule>) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.EMAIL}/${rule.id}`);
  }

  onDismissEmailInboxModal() {
    this.getInboundEmailRules();
    this.showEmailInboxModal = false;
    this.navController.navigateForward(this.router.url, {
      queryParams: {},
    });
  }

  onRuleChannelChanged(rule, slackChannel) {
    rule.channelId = slackChannel?.id ?? null;
    rule.slackChannel = slackChannel;

    if (rule.id) {
      this.save(rule);
    }
  }

  autoresponderDidChange(rule: Partial<InboundEmailRule>, autoresponder: any) {
    rule.autoresponder = autoresponder;
  }

  sortBlocklist(rule: Partial<InboundEmailRule>): string[] {
    if (!rule.blocklist) {
      return [];
    }

    // Safely copy and sort so we don't mutate the original array
    return Array.from(
      new Set([...rule.blocklist].map((b) => b.toLowerCase().trim()).sort((a, b) => a.localeCompare(b))),
    );
  }
}
